/**
 * How a monitoring figure is written down.
 *
 * The ledger, the covenant table, the gaps list and the lock dialog all print
 * the same numbers, so the unit rules live in one place: money in USD millions
 * to one decimal, multiples to two with an `x`, rates as a percentage of the
 * fraction the server sends. A missing value is an em dash everywhere — "0"
 * and "not found" are different answers and must not look alike.
 *
 * DOP-451 ships the identical three helpers as
 * `components/Monitoring/monitoringFormat.ts`; the signatures and semantics are
 * deliberately the same so the orchestrator can collapse them at merge.
 */

import type { MonitoringUnit } from "../../types/monitoring";

/** What a cell shows when there is no number to show. */
export const NO_VALUE = "—";

const MONTHS = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
];

/** A figure in its unit: `130.0`, `4.85x`, `8.3%`. */
export function formatFigure(
	value: number | null | undefined,
	unit: MonitoringUnit,
): string {
	if (value === null || value === undefined || !Number.isFinite(value)) {
		return NO_VALUE;
	}
	switch (unit) {
		case "usd_m":
			return value.toFixed(1);
		case "x":
			return `${value.toFixed(2)}x`;
		case "pct":
			return `${(value * 100).toFixed(1)}%`;
		default:
			return String(value);
	}
}

/** An epoch-millisecond timestamp as a date: `Sep 7, 2026`. */
export function formatEpochMs(ms: number | null | undefined): string {
	if (ms === null || ms === undefined || !Number.isFinite(ms)) return NO_VALUE;
	const date = new Date(ms);
	if (Number.isNaN(date.getTime())) return NO_VALUE;
	return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

/**
 * An ISO `YYYY-MM-DD` date as `Dec 31, 2025`.
 *
 * Split rather than parsed: `new Date("2025-12-31")` is UTC midnight, which
 * prints as the 30th for every user west of Greenwich — and every user here is
 * in the US.
 */
export function formatIsoDate(iso: string | null | undefined): string {
	if (!iso) return NO_VALUE;
	const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
	if (!match) return iso;
	const month = Number(match[2]);
	if (month < 1 || month > 12) return iso;
	return `${MONTHS[month - 1]} ${Number(match[3])}, ${match[1]}`;
}

/**
 * The change from the prior period, in the form that reads for the unit:
 * a percentage move for money, a plain difference for multiples, and
 * percentage points for rates. Null whenever either side is missing — a delta
 * against nothing is not zero.
 */
export function formatDelta(
	prior: number | null | undefined,
	current: number | null | undefined,
	unit: MonitoringUnit,
): string {
	if (
		prior === null ||
		prior === undefined ||
		current === null ||
		current === undefined ||
		!Number.isFinite(prior) ||
		!Number.isFinite(current)
	) {
		return NO_VALUE;
	}
	if (unit === "usd_m") {
		if (prior === 0) return NO_VALUE;
		const pct = (current / prior - 1) * 100;
		return `${pct >= 0 ? "+" : "−"}${Math.abs(pct).toFixed(1)}%`;
	}
	const diff = current - prior;
	if (unit === "pct") {
		const points = diff * 100;
		return `${points >= 0 ? "+" : "−"}${Math.abs(points).toFixed(1)}pp`;
	}
	return `${diff >= 0 ? "+" : "−"}${Math.abs(diff).toFixed(2)}x`;
}

/** A magnitude without a sign, for "tightened 0.39x" / "widened 0.12x". */
export function formatMagnitude(
	value: number | null | undefined,
	unit: MonitoringUnit,
): string {
	if (value === null || value === undefined || !Number.isFinite(value)) {
		return NO_VALUE;
	}
	return formatFigure(Math.abs(value), unit);
}

/**
 * The server's own `delta_pct` as text: `+4.2%`, `−9.5%`.
 *
 * A FRACTION comes in (−4/42), not a percentage, and it is the compare
 * endpoint's arithmetic rather than this file's — `formatDelta` re-derives a
 * move from two values and would disagree with it on a figure the server
 * computed differently. Signed with the same unicode minus as `formatDelta`,
 * so a column of them lines up.
 */
export function formatDeltaPct(fraction: number | null | undefined): string {
	if (
		fraction === null ||
		fraction === undefined ||
		!Number.isFinite(fraction)
	) {
		return NO_VALUE;
	}
	const pct = fraction * 100;
	return `${pct >= 0 ? "+" : "−"}${Math.abs(pct).toFixed(1)}%`;
}
